import { textGray } from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import React, { useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import {
  PlaylistItemProps,
  PlaylistProps,
  QuizAllTagSet,
} from '@osmosis/shared/types/playlist';
import { UserProductComponentType } from '@elsevier-health/osmosis-shared/types/user';
import PopupMenu from '../../../components/PopupMenu';
import useModal from '../../../hooks/useModal';
import PopupMenuRow from '../../../components/PopupMenuRow';
import { faBolt } from '@fortawesome/pro-regular-svg-icons/faBolt';
import { faStethoscope } from '@fortawesome/pro-regular-svg-icons/faStethoscope';
import { usePagesContext } from '../../../context/PagesProvider';
import { getPlaylistQuizUrl } from '../../../lib/frontend/quiz/link';
import { useUserContext } from '../../../context/UserContext';
import ExpiredUserLockoutModal from '../../../components/learn/ExpiredUserModal';
import { ExpiredUserModalType } from '../../../constants/ExpiredUserModalConfig';
import { useFetch } from '../../../context/FetchProvider';
import { LoadingSpinner } from '@osmosis/tailwind-ui/components/LoadingSpinner';

type Props = {
  playlist: PlaylistProps;
};

/**
 * PlaylistQuizAllButton.
 */
const PlaylistQuizAllButton = (props: Props) => {
  const { playlist } = props;

  const { hasPrime, hasProductComponent } = useUserContext();
  const { get } = useFetch();

  const [quizAllData, setQuizAllData] = useState<QuizAllTagSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const quizAllBtn = useRef<HTMLElement>(null);
  const popup = useModal();
  const nonPrimeModal = useModal();
  const { push } = usePagesContext();
  const intl = useIntl();
  const flashcards: number[] = playlist.list.reduce(
    (fcs: number[], item: PlaylistItemProps) => [
      ...fcs,
      ...(item.data.quizData?.fc || []),
    ],

    [] as number[],
  );

  const handleQuizFlashcards = () => {
    push(getPlaylistQuizUrl(playlist, 'flashcard'));
  };

  const handleQuizQuestionSet = (set: string) => {
    push(getPlaylistQuizUrl(playlist, set));
  };

  const hasFlashCards = hasProductComponent?.(
    UserProductComponentType.FLASHCARDS,
  );

  const filterQuizData = [
    ...new Set(
      playlist.list.reduce((acc: QuizAllTagSet[], item) => {
        quizAllData.forEach((quiz) => {
          if (
            item.data.quizData?.ques[quiz.type] &&
            item.data.quizData?.ques[quiz.type].list.length > 0
          ) {
            acc.push(quiz);
          }
        });
        return acc;
      }, []),
    ),
  ];

  const popupMenuItems = [
    ...(flashcards?.length && hasFlashCards
      ? [
          {
            id: 'flashcards',
            as: 'div',
            tabIndex: 0,
            icon: faBolt,
            text: intl.formatMessage({
              id: 'quiz.all.flashcards',
              defaultMessage: 'Flashcards',
            }),
            onClick: handleQuizFlashcards,
          },
        ]
      : []),
    ...(Array.isArray(filterQuizData) && filterQuizData?.length
      ? [...filterQuizData].map((set) => ({
          id: `question-${set.name}`,
          as: 'div',
          tabIndex: 0,
          icon: faStethoscope,
          text: set.overrideName || set.tag || set.name,
          onClick: () => {
            handleQuizQuestionSet(set.type);
          },
        }))
      : []),
  ];

  const handleQuizAll = async () => {
    if (!hasPrime) {
      nonPrimeModal.show();
      return;
    }

    if (!hasFetched && !isLoading) {
      setIsLoading(true);
      try {
        const { data } = await get<{ quizAllData: QuizAllTagSet[] }>(
          '/getPlaylistQuizData',
        );
        setQuizAllData(data?.quizAllData ?? []);
        setHasFetched(true);
      } finally {
        setIsLoading(false);
      }
    }

    popup.toggle();
  };

  const hasQuizQuestions = playlist.list.some((item) =>
    Object.values(item.data.quizData?.ques ?? {}).some(
      (set) => (set as { list?: number[] })?.list?.length,
    ),
  );
  const showQuizAllButton =
    hasQuizQuestions || (!!flashcards.length && !!hasFlashCards);

  return (
    <>
      <ExpiredUserLockoutModal
        {...nonPrimeModal}
        modalType={ExpiredUserModalType.PLAYLIST}
      />
      {showQuizAllButton && (
        <Button
          ref={quizAllBtn}
          variant="outline"
          onClick={handleQuizAll}
          data-testid="quiz-all-playlist-btn"
          // eslint-disable-next-line @nx/workspace/tailwind-style-enforcement
          className={`${textGray}`}
        >
          <PopupMenu
            {...popup}
            renderControl={true}
            isPortal={false}
            items={popupMenuItems}
            keyExtractor={(item) => item.id}
            renderItem={(item, i) => (
              <Box
                data-testid="popup-menu.item"
                role="button"
                key={`quizall-${i}`}
              >
                <PopupMenuRow
                  data-testid="popup-menu.row"
                  item={item}
                  onClick={(item, event) => {
                    event?.preventDefault();
                    item?.onClick?.();
                  }}
                />
              </Box>
            )}
            showHeader={false}
          >
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <FormattedMessage
                id="learn.playlist.Quiz-all"
                defaultMessage="Quiz all"
              />
            )}
          </PopupMenu>
        </Button>
      )}
    </>
  );
};

export default PlaylistQuizAllButton;
